/**
 * @Description 普通地址组件
 */

import { FC, useEffect, useRef, useState } from 'react';
import { Row, Col, Spin } from 'antd';
import { useMethods } from '@lhb/hook';
import { keywordPrompt, getLngLatAddress, SEARCH_POI_ICON_SOLID_RED } from '@/common/utils/map';
import { debounce, isArray } from '@lhb/func';
import { parseValueCatch } from '@/common/components/business/DynamicComponent/config';
// import cs from 'classnames';
import styles from './index.module.less';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import AMap from '@/common/components/AMap/index';
import { AddressChangeParams } from '.';
import useCurLocation from '@/common/components/Map/V2AMap/hooks/useCurLocation';
import { pcdIdentification } from '../../customize';
import { v4 } from 'uuid';
import { defaultMapStyle } from '@/layout/ts-config';

const NormalAddress: FC<{
  formItemData,
  form,
  disabled,
  required,
  /** 详细地址的特殊赋值逻辑 */
  addressChange,
  mapZoomChange,
  mapMoveChange,
  mapConfig,
}> = ({
  formItemData,
  form,
  disabled,
  required,
  addressChange, // 详细地址的特殊赋值逻辑
  mapZoomChange,
  mapMoveChange,
  mapConfig,
}) => {
  const centerMarkerRef: any = useRef();
  const [amapIns, setAmapIns] = useState<any>(null); // 地图实例
  const [options, setOptions] = useState<any[]>([]); // poi结果
  const [cityInfo, setCityInfo] = useState<any>({}); // 高德返回的当前城市信息
  const [fetching, setFetching] = useState(false); // 是否在搜索中
  const locationInfo = useCurLocation(amapIns, {
    // 编辑的时候不定位到具体位置，只在第一次填写表单的时候才定位
    zoomToAccuracy: !formItemData?.textValue, // 获取到定位后是否缩放地图
    panToLocation: !formItemData?.textValue, // 定位成功后是否自动移动到响应位置
  });
  const locationInfoRef: any = useRef(locationInfo);

  useEffect(() => {
    // 处理切换tab时因组件的重新渲染导致的options清空的情况
    if (isArray(options) && options.length) return;
    const curVal = parseValueCatch(formItemData);
    if (!curVal) return;
    form.setFieldValue(formItemData.identification, curVal?.poiName || curVal?.address || '');

    const params: AddressChangeParams = {
      isInitChange: true, // 编辑表单回显时特殊处理的标记，区分手动变更地址的情况
      lng: curVal.longitude,
      lat: curVal.latitude,
      cityName: '', // curVal.cityName,
      poiId: curVal.poiId,
      name: curVal.address,
      identification: formItemData.identification
    };
    if (!amapIns) return; // 等待地图实例创建完之后才能调用对应方法
    addressChange && addressChange(params);
    const targetPCDVal = form.getFieldValue(pcdIdentification); // 查找省市区组件是否有值
    // if (lng && lat) addCenterMarker(lng, lat);
    if (!targetPCDVal) { // 没有值
      // 特殊场景，客流计数那里创建机会点时要默认带入
      addressChange && addressChange({
        lng: curVal.longitude,
        lat: curVal.latitude,
        // 注释的原因是这里的逻辑有问题，不能拿当前定位的城市去查接口拿省市区id
        cityName: '', // cityInfo?.city,
        poiId: null,
        name: curVal.address,
        identification: formItemData.identification,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formItemData, options, amapIns]);

  // 监听地图移动
  const mapChange = debounce(() => {
    mapMoveChange?.();
  }, 100);

  // 监听地图缩放
  const debounceMapZoomChange = debounce(() => {
    mapZoomChange?.();
  }, 100);

  useEffect(() => {
    if (!amapIns) return;
    amapIns.on('click', setCenter);
    amapIns.on('moveend', mapChange);
    amapIns.on('zoomend', debounceMapZoomChange);

    return () => {
      amapIns.off('click', setCenter);
      amapIns?.off('moveend', mapChange);
      amapIns?.off('zoomend', debounceMapZoomChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amapIns]);

  useEffect(() => {
    locationInfoRef.current = locationInfo;
    const { lng, lat } = locationInfo || {};
    if (+lng && +lat && !formItemData?.textValue) {
      locationInfo.city = locationInfo.cityName;
      setCityInfo(locationInfo);
      addCenterMarker(+lng, +lat);
    }
  }, [locationInfo]);

  const {
    loadedMapHandle,
    searchHandle,
    addCenterMarker,
    changeHandle,
    setCenter
  } = useMethods({
    // 地图加载完毕
    loadedMapHandle(mapIns: any) {
      if (!mapIns) return;
      setAmapIns(mapIns);
      const { textValue } = formItemData;
      if (textValue) {
        try {
          const info = parseValueCatch(formItemData);
          const { longitude, latitude } = info;
          if (!(longitude && latitude)) return;
          setCityInfo({
            ...info,
            city: info.cityName
          });
          addCenterMarker(longitude, latitude);
          return;
        } catch (error) {
          console.log(`地址组件的地图回显出错`, error);
        }
      }
      // 以下逻辑迁移到locationInfo的useEffect里了

      // 浏览器定位
      // getCurPosition(mapIns, {
      //   // https://lbs.amap.com/api/javascript-api-v2/documentation#geolocation
      //   // 是否显示定位按钮
      //   showButton: false,
      //   showCircle: false,
      //   showMarker: false
      // }).then((res: any) => {
      //   setCityInfo(res);
      //   const { position } = res;
      //   if (!(isArray(position))) return;
      //   addCenterMarker(position[0], position[1]);
      // });
    },
    // 添加详细地址的marker
    addCenterMarker(lng, lat) {
      // 更新位置
      if (centerMarkerRef.current) {
        centerMarkerRef.current.setPosition([lng, lat]);
        amapIns.setFitView(centerMarkerRef.current);
        return;
      }
      const customIcon = new window.AMap.Icon({
        // 图标尺寸
        size: new window.AMap.Size(40, 40),
        // 图标的取图地址
        image: SEARCH_POI_ICON_SOLID_RED,
        // 图标所用图片大小
        imageSize: new window.AMap.Size(40, 40),
        // 图标取图偏移量
        // imageOffset: new AMap.Pixel(0, 10)
      });

      const marker = new window.AMap.Marker({
        position: new window.AMap.LngLat(lng, lat),
        // 将一张图片的地址设置为 icon
        icon: customIcon,
        // 设置了 icon 以后，设置 icon 的偏移量，以 icon 的 [center bottom] 为原点
        offset: new window.AMap.Pixel(-40 / 2, -40),
      });
      amapIns.add(marker);
      amapIns.setFitView(marker);
      centerMarkerRef.current = marker;
    },
    // 关键词搜索
    searchHandle: debounce((keyword: string) => {
      // 地图没加载完成时，不搜索
      if (!amapIns) return;
      setOptions([]);
      setFetching(true);

      keywordPrompt(keyword, {
        city: locationInfoRef.current?.cityName
      }).then((pois) => {
        setFetching(false);
        if (isArray(pois)) {
          setOptions(pois);
          return;
        }
      }).finally(() => {
        setFetching(false);
      });
    }, 700),
    // 选择搜索的地址
    async changeHandle(id: string, option: any) {
      // clear时option是null
      if (!option) return;
      // console.log(`选择的地址：`, option);
      const { location, id: poiId, name } = option;
      const { lng, lat } = location || {};
      if (lng && lat) addCenterMarker(lng, lat);
      const params: AddressChangeParams = {
        lng,
        lat,
        cityName: cityInfo?.city,
        poiId,
        name,
        identification: formItemData.identification
      };
      addressChange && addressChange(params);
    },
    // 点击地图设置中心点
    async setCenter(e: any) {
      if (disabled) return;

      // console.log(`eee`, e);
      const { lnglat } = e;
      const { lng, lat } = lnglat;
      amapIns.setCenter(lnglat);
      if (!(lng && lat)) return;
      addCenterMarker(lng, lat);
      // 将点击的位置解析出具体位置
      const res: any = await getLngLatAddress(lnglat, '', false);
      if (!res) return;
      const { formattedAddress, addressComponent } = res;
      const { city: cityName } = addressComponent || {};
      // 手动构造options(注意下拉选择触发的changeHandle逻辑)
      setOptions([{
        id: v4(),
        name: formattedAddress,
        location: {
          lng,
          lat,
        }
      }]);
      form.setFieldValue(formItemData.identification, formattedAddress || '');
      const params: AddressChangeParams = {
        lng,
        lat,
        cityName,
        poiId: null,
        name: formattedAddress,
        identification: formItemData.identification
      };
      (!disabled && addressChange) && addressChange(params);
    }
  });

  return (
    <>
      <Row gutter={24}>
        <Col span={24}>
          <V2FormSelect
            required={required}
            label={formItemData.anotherName || formItemData.name}
            name={formItemData.identification}
            disabled={disabled}
            options={options}
            config={{
              showSearch: true,
              filterOption: false,
              fieldNames: {
                label: 'name',
                value: 'id'
              },
              notFoundContent: fetching ? <Spin size='small' /> : null,
              onSearch: searchHandle,
              onChange: changeHandle
            }}
          />
        </Col>
      </Row>
      <div className={styles.mapCon}>
        <AMap
          loaded={loadedMapHandle}
          mapOpts={{
            mapStyle: `amap://styles/${mapConfig?.bg || defaultMapStyle}`
          }}
          plugins={[
            'AMap.Geocoder',
            // 'AMap.PlaceSearch',
            'AMap.AutoComplete',
          ]}
        />
      </div>
    </>
  );
};

export default NormalAddress;
